// Función auxiliar extraída de ProcesoPlanilla.js

import {
  calcularProvisiónVacaciones,
  calcularProvisiónGratificaciones,
  calcularHorasExtras,
  calcularBonosRemunerativos,
} from "./calculosLaborales.js";

// ========== VALIDACIONES COMUNES ==========

/**
 * Valida que un valor sea un número positivo
 * @param {*} valor - Valor a validar
 * @param {string} nombreCampo - Nombre del campo para mensajes de error
 * @returns {boolean} - True si es válido
 */
export const validarNumeroPositivo = (valor, nombreCampo) => {
  if (typeof valor !== 'number' || isNaN(valor) || valor <= 0) {
    console.warn(`${nombreCampo} debe ser un número positivo`, { valor, tipo: typeof valor });
    return false;
  }
  return true;
};

/**
 * Valida que un valor sea un porcentaje válido (0-100%)
 * @param {*} valor - Valor a validar
 * @param {string} nombreCampo - Nombre del campo para mensajes de error
 * @returns {boolean} - True si es válido
 */
export const validarPorcentaje = (valor, nombreCampo) => {
  if (typeof valor !== 'number' || isNaN(valor) || valor < 0 || valor > 1) {
    console.warn(`${nombreCampo} debe ser un porcentaje válido (0-100%)`, { valor, tipo: typeof valor });
    return false;
  }
  return true;
};

/**
 * Valida formato de periodo YYYY-MM
 * @param {string} periodo - Periodo a validar
 * @returns {boolean} - True si es válido
 */
export const validarPeriodo = (periodo) => {
  if (!periodo || typeof periodo !== 'string') return false;
  return /^\d{4}-\d{2}$/.test(periodo);
};

/**
 * Valida que un empleado tenga los campos críticos
 * @param {Object} empleado - Datos del empleado
 * @returns {Object} - { valido: boolean, errores: string[] }
 */
export const validarEmpleado = (empleado) => {
  const errores = [];

  if (!empleado || typeof empleado !== 'object') {
    errores.push("Empleado debe ser un objeto válido");
    return { valido: false, errores };
  }

  if (!empleado.id || typeof empleado.id !== 'string') {
    errores.push("ID del empleado es requerido");
  }

  if (!empleado.nombres || typeof empleado.nombres !== 'string' || empleado.nombres.trim().length === 0) {
    errores.push("Nombre del empleado es requerido");
  }

  if (!validarNumeroPositivo(empleado.sueldo_base, "Sueldo base")) {
    errores.push("Sueldo base debe ser un número positivo");
  }

  return { valido: errores.length === 0, errores };
};

/**
 * Valida configuración de planilla
 * @param {Object} config - Configuración a validar
 * @returns {Object} - { valido: boolean, errores: string[] }
 */
export const validarConfiguracionPlanilla = (config) => {
  const errores = [];

  if (!config || typeof config !== 'object') {
    errores.push("Configuración debe ser un objeto válido");
    return { valido: false, errores };
  }

  // Validar UIT
  if (!validarNumeroPositivo(config.uit, "UIT")) {
    errores.push("UIT debe ser un número positivo");
  }

  // Validar sueldo mínimo
  if (!validarNumeroPositivo(config.sueldo_minimo, "Sueldo mínimo")) {
    errores.push("Sueldo mínimo debe ser un número positivo");
  }

  // Validar porcentajes AFP
  if (config.afp_integra) {
    const afp = config.afp_integra;
    if (!validarPorcentaje(afp.fondo, "Fondo AFP Integra")) errores.push("Fondo AFP Integra inválido");
    if (!validarPorcentaje(afp.seguro, "Seguro AFP Integra")) errores.push("Seguro AFP Integra inválido");
    if (!validarPorcentaje(afp.comision, "Comisión AFP Integra")) errores.push("Comisión AFP Integra inválido");
  }

  // Validar ESSALUD
  if (!validarPorcentaje(config.porcentaje_essalud, "ESSALUD")) {
    errores.push("Porcentaje ESSALUD debe ser válido");
  }

  // Validar ONP
  if (!validarPorcentaje(config.porcentaje_onp, "ONP")) {
    errores.push("Porcentaje ONP debe ser válido");
  }

  return { valido: errores.length === 0, errores };
};

// ========== FUNCIONES EXISTENTES ==========

export const getUltimoDiaMes = (periodo) => {
  const [year, month] = periodo.split("-");
  return new Date(year, month, 0).getDate();
};

export const getDescripcionRegimen = (regimen) => {
  switch (regimen) {
    case "MYPE_MICRO":
      return "Microempresa: No aplica gratificación ni CTS por defecto. Vacaciones de 15 días.";
    case "MYPE_PEQUENA":
      return "Pequeña empresa: Gratificación y CTS al 50%, vacaciones de 15 días.";
    case "AGRARIO":
      return "Régimen Agrario: Gratificación y CTS en remuneración diaria, descanso vacacional 30 días.";
    case "CONSTRUCCION_CIVIL":
      return "Construcción Civil: Incluye bonificaciones específicas según condiciones de trabajo.";
    case "CAS":
      return "CAS: Aguinaldo en julio y diciembre, vacaciones de 30 días, no incluye CTS.";
    default:
      return "Régimen General: Gratificación y CTS completas, vacaciones de 30 días.";
  }
};

export const obtenerDatosAsistencia = async (empleadoId, periodo, usuario, empleadoData = null) => {
  try {
    if (!empleadoId || !periodo) {
      console.warn("obtenerDatosAsistencia: empleadoId o periodo inválido");
      return {
        faltas: { injustificadas: 0, justificadas: 0 },
        permisos: { sin_goce: 0, con_goce: 0, descanso_medico: 0 },
        tardanzas: [],
        asistencias: [],
        resumen_rol: null,
      };
    }

    const { collection, query, where, getDocs, getFirestore, doc, getDoc } = await import(
      "firebase/firestore"
    );
    const firebaseApp = (await import("../firebase/credenciales")).default;
    const db = getFirestore(firebaseApp);

    const ocurrenciasRef = collection(db, "pl_ocurrencias");
    const q = query(
      ocurrenciasRef,
      where("ruc_empresa", "==", usuario.ruc),
      where("empleado_id", "==", empleadoId),
      where("periodo", "==", periodo)
    );

    const snapshot = await getDocs(q);

    const pad2 = (value) => String(value).padStart(2, "0");
    const createDateKey = (year, month, day) => `${year}-${pad2(month)}-${pad2(day)}`;

    const mapRoleCodeToOcurrencia = (code, fecha) => {
      const base = {
        fecha,
        activo: true,
        estado: "manual_importado",
        horas_afectadas: 8,
        origen: "rol_turnos",
      };

      switch (code) {
        case "F":
          return { ...base, tipo: "falta", justificado: false };
        case "M":
          return { ...base, tipo: "descanso_medico" };
        case "V":
          return { ...base, tipo: "vacaciones" };
        case "PG":
          return { ...base, tipo: "licencia_con_goce", con_goce: true };
        case "PS":
          return { ...base, tipo: "licencia_sin_goce", con_goce: false };
        case "P":
          return { ...base, tipo: "permiso", con_goce: false };
        case "D":
          return { ...base, tipo: "descanso", horas_afectadas: 0 };
        case "T":
        default:
          return { ...base, tipo: "asistencia", horas_afectadas: 8 };
      }
    };

    const getRoleCodeForDate = (config, dateObj) => {
      const dateKey = createDateKey(
        dateObj.getFullYear(),
        dateObj.getMonth() + 1,
        dateObj.getDate()
      );

      if (config?.excepciones && config.excepciones[dateKey]) {
        return String(config.excepciones[dateKey]);
      }

      if (!config?.fechaInicio) return "T";
      const startDate = new Date(`${config.fechaInicio}T00:00:00`);
      if (Number.isNaN(startDate.getTime())) return "T";

      const diasTrabajo = Number(config.diasTrabajo) || 24;
      const diasDescanso = Number(config.diasDescanso) || 14;
      const cycleLength = diasTrabajo + diasDescanso;
      if (cycleLength <= 0) return "T";

      const msPerDay = 24 * 60 * 60 * 1000;
      const diffDays = Math.floor((dateObj.getTime() - startDate.getTime()) / msPerDay);
      let positionInCycle = diffDays % cycleLength;
      if (positionInCycle < 0) {
        positionInCycle = (cycleLength + positionInCycle) % cycleLength;
      }

      return positionInCycle < diasTrabajo ? "T" : "D";
    };

    const obtenerDniEmpleado = async () => {
      if (empleadoData?.dni) return String(empleadoData.dni);
      const empleadoRef = doc(db, "pl_empleados", empleadoId);
      const empleadoSnap = await getDoc(empleadoRef);
      if (empleadoSnap.exists()) {
        const data = empleadoSnap.data();
        if (data?.dni) return String(data.dni);
      }
      return null;
    };

    const ocurrenciasRolPorFecha = {};
    let resumenRol = null;

    try {
      const dniEmpleado = await obtenerDniEmpleado();
      if (dniEmpleado && usuario?.ruc) {
        const unique = (arr) => Array.from(new Set((arr || []).filter(Boolean).map((v) => String(v))));
        let rolData = null;
        let rolSedeId = "general";

        // 1) Intentar resolver por memberships (nuevo esquema por sede)
        const membershipRef = doc(db, "grupos", String(usuario.ruc), "memberships", String(dniEmpleado));
        const membershipSnap = await getDoc(membershipRef);
        const memberSedes = unique(membershipSnap.exists() ? (membershipSnap.data()?.sedeIds || []) : []);

        const hintedSedes = unique([
          empleadoData?.sede_id,
          empleadoData?.sedeId,
        ]);
        const sedesCandidatas = unique([...memberSedes, ...hintedSedes]);

        for (const sedeId of sedesCandidatas) {
          const rolRefSede = doc(
            db,
            "pl_rol_turnos",
            String(usuario.ruc),
            "sedes",
            String(sedeId),
            "rol",
            String(dniEmpleado)
          );
          const rolSnapSede = await getDoc(rolRefSede);
          if (rolSnapSede.exists()) {
            rolData = rolSnapSede.data();
            rolSedeId = String(rolData?.sedeId || sedeId);
            break;
          }
        }

        // 2) Fallback: esquema legacy
        if (!rolData) {
          const rolRefLegacy = doc(db, "pl_rol_turnos", String(usuario.ruc), "rol", String(dniEmpleado));
          const rolSnapLegacy = await getDoc(rolRefLegacy);
          if (rolSnapLegacy.exists()) {
            rolData = rolSnapLegacy.data();
            rolSedeId = String(rolData?.sedeId || "general");
          }
        }

        if (rolData) {
          const [anio, mes] = periodo.split("-").map(Number);
          const diasMes = new Date(anio, mes, 0).getDate();
          let diasTrabajoProgramados = 0;
          let diasDescansoProgramados = 0;
          let diasTrabajoEfectivo = 0;
          const codigos = { T: 0, D: 0, F: 0, M: 0, V: 0, PG: 0, PS: 0, P: 0 };

          for (let d = 1; d <= diasMes; d += 1) {
            const fechaObj = new Date(anio, mes - 1, d, 0, 0, 0);
            const fecha = createDateKey(anio, mes, d);
            const code = getRoleCodeForDate(rolData, fechaObj);
            const normalizedCode = codigos[code] !== undefined ? code : "T";
            codigos[normalizedCode] += 1;

            if (normalizedCode === "D") {
              diasDescansoProgramados += 1;
            } else {
              diasTrabajoProgramados += 1;
            }
            if (normalizedCode === "T") {
              diasTrabajoEfectivo += 1;
            }

            ocurrenciasRolPorFecha[fecha] = mapRoleCodeToOcurrencia(normalizedCode, fecha);
          }

          resumenRol = {
            fuente: "pl_rol_turnos",
            sede_id: rolSedeId,
            dias_trabajo_programados: diasTrabajoProgramados,
            dias_descanso_programados: diasDescansoProgramados,
            dias_trabajo_efectivo: diasTrabajoEfectivo,
            dias_trabajo_ciclo: Number(rolData?.diasTrabajo || 0),
            dias_descanso_ciclo: Number(rolData?.diasDescanso || 0),
            codigos,
          };
        }
      }
    } catch (rolError) {
      console.warn("No se pudo integrar pl_rol_turnos en asistencia:", rolError);
    }

    // DEDUPLICACIÓN POR FECHA: Evitar que existan múltiples ocurrencias para el mismo día
    // (Ejemplo: una detectada automáticamente como 'licencia' y luego una manual como 'vacaciones')
    const ocurrenciasPorFecha = {};
    
    snapshot.forEach((doc) => {
      const data = doc.data();
      const fecha = data.fecha;
      const ocurrencia = { id: doc.id, ...data };

      // Incluir si está activo (aprobado) O si es detectado automáticamente o importado
      const estadosPermitidosSinAprobar = ["detectado_automatico", "manual_importado", "manual"];
      if (!ocurrencia.activo && !estadosPermitidosSinAprobar.includes(ocurrencia.estado)) {
        return;
      }

      if (!ocurrenciasPorFecha[fecha]) {
        ocurrenciasPorFecha[fecha] = [ocurrencia];
      } else {
        ocurrenciasPorFecha[fecha].push(ocurrencia);
      }
    });

    const faltas = { injustificadas: 0, justificadas: 0 };
    const permisos = { sin_goce: 0, con_goce: 0, descanso_medico: 0 };
    const tardanzas = [];
    const asistencias = [];
    const ocurrenciasFinalesPorFecha = { ...ocurrenciasRolPorFecha };

    // Priorizar ocurrencias manuales/detectadas de pl_ocurrencias por encima del rol de turnos
    Object.entries(ocurrenciasPorFecha).forEach(([fecha, listaOcurrencias]) => {
      let ocurrencia;
      if (listaOcurrencias.length > 1) {
        const manual = listaOcurrencias.find(o => o.estado !== "detectado_automatico");
        if (manual) {
          ocurrencia = manual;
        } else {
          const vac = listaOcurrencias.find(o => o.tipo === "vacaciones");
          ocurrencia = vac || listaOcurrencias[0];
        }
      } else {
        ocurrencia = listaOcurrencias[0];
      }
      ocurrenciasFinalesPorFecha[fecha] = ocurrencia;
    });

    // Procesar ocurrencias finales (rol + ocurrencias manuales)
    Object.values(ocurrenciasFinalesPorFecha).forEach((ocurrencia) => {
      asistencias.push(ocurrencia);

      switch (ocurrencia.tipo) {
        case "falta":
          if (!ocurrencia.justificado) {
            const diasDeHoras = (Number(ocurrencia.horas_afectadas) || 0) / 8;
            faltas.injustificadas += diasDeHoras;
          } else {
            const diasDeHoras = (Number(ocurrencia.horas_afectadas) || 0) / 8;
            faltas.justificadas += diasDeHoras;
          }
          break;

        case "descanso_medico":
          permisos.descanso_medico += (Number(ocurrencia.horas_afectadas) || 0) / 8;
          break;
          
        case "licencia_con_goce":
          permisos.con_goce += (Number(ocurrencia.horas_afectadas) || 0) / 8;
          break;

        case "vacaciones":
          // Las vacaciones se manejan por separado en su propio campo en calcularPlanillaMensual
          break;

        case "permiso":
        case "licencia":
        case "licencia_sin_goce":
          if (ocurrencia.con_goce) {
            permisos.con_goce += (Number(ocurrencia.horas_afectadas) || 0) / 8;
          } else {
            permisos.sin_goce += (Number(ocurrencia.horas_afectadas) || 0) / 8;
          }
          break;

        case "tardanza":
          tardanzas.push({
            fecha: ocurrencia.fecha,
            minutos: ocurrencia.minutos_tardanza || 0,
          });
          break;
      }
    });

    return { 
      faltas, 
      permisos, 
      tardanzas, 
      asistencias,
      dias_descanso_medico: permisos.descanso_medico,
      resumen_rol: resumenRol,
    };
  } catch (error) {
    console.error("Error en obtenerDatosAsistencia:", error);
    return {
      faltas: { injustificadas: 0, justificadas: 0 },
      permisos: { sin_goce: 0, con_goce: 0, descanso_medico: 0 },
      tardanzas: [],
      asistencias: [],
      resumen_rol: null,
    };
  }
};


/**
 * Obtiene la última gratificación pagada a un empleado desde la base de datos.
 */
export const obtenerUltimaGratificacion = async (
  empleadoId,
  periodoCts,
  usuario
) => {
  console.log("🔍 obtenerUltimaGratificacion - Iniciando búsqueda", {
    empleadoId,
    periodoCts,
    usuarioRuc: usuario?.ruc
  });

  try {
    const { collection, query, where, getDocs, and, getFirestore } = await import(
      "firebase/firestore"
    );
    const firebaseApp = (await import("../firebase/credenciales")).default;
    const db = getFirestore(firebaseApp);

    const [anioCts, mesCts] = periodoCts.split("-").map(Number);
    let periodoGratiBuscado;

    // Determinar el periodo de la gratificación anterior
    if (mesCts >= 1 && mesCts <= 5) {
      // CTS de Mayo usa la grati de Diciembre del año anterior
      periodoGratiBuscado = `${anioCts - 1}-12`;
    } else if (mesCts >= 6 && mesCts <= 11) {
      // CTS de Noviembre usa la grati de Julio
      periodoGratiBuscado = `${anioCts}-07`;
    } else {
      // CTS de Diciembre (caso especial) usa la grati de Julio
      periodoGratiBuscado = `${anioCts}-07`;
    }

    console.log("🔍 obtenerUltimaGratificacion - Periodo de gratificación buscado:", periodoGratiBuscado);

    if (!periodoGratiBuscado) return 0;

    const detallesRef = collection(db, "pl_planilla_detalle");
    const q = query(
      detallesRef,
      and(
        where("ruc_empresa", "==", usuario.ruc),
        where("empleado_id", "==", empleadoId),
        where("periodo", "==", periodoGratiBuscado)
      )
    );

    console.log("🔍 obtenerUltimaGratificacion - Ejecutando consulta a Firestore");
    const snapshot = await getDocs(q);
    console.log("🔍 obtenerUltimaGratificacion - Documentos encontrados:", snapshot.size);

    if (snapshot.empty) {
      console.warn(
        `No se encontró detalle de gratificación para el empleado ${empleadoId} en el periodo ${periodoGratiBuscado}`
      );
      return 0;
    }

    const detalleGratificacion = snapshot.docs[0].data();
    const gratificacion = detalleGratificacion.gratificacion || 0;
    console.log("🔍 obtenerUltimaGratificacion - Gratificación encontrada:", gratificacion);

    return gratificacion;
  } catch (error) {
    console.error("Error al obtener la última gratificación:", error);
    return 0;
  }
};

/**
 * Obtiene los ingresos y retenciones de 5ta categoría acumulados en el año.
 */
export const obtenerAcumuladosRenta5ta = async (
  empleadoId,
  periodoActual,
  usuario
) => {
  try {
    if (!usuario || !usuario.ruc) {
      console.error("Usuario o ruc inválido en obtenerAcumuladosRenta5ta");
      return { ingresosAcumulados: 0, retencionesAcumuladas: 0 };
    }

    const { collection, query, where, getDocs, and, getFirestore } = await import(
      "firebase/firestore"
    );
    const firebaseApp = (await import("../firebase/credenciales")).default;
    const db = getFirestore(firebaseApp);

    const anioActual = periodoActual.split("-")[0];
    const mesActual = parseInt(periodoActual.split("-")[1], 10);

    const periodosAnteriores = [];
    for (let i = 1; i < mesActual; i++) {
      periodosAnteriores.push(`${anioActual}-${String(i).padStart(2, "0")}`);
    }

    if (periodosAnteriores.length === 0) {
      return { ingresosAcumulados: 0, retencionesAcumuladas: 0 };
    }

    const detallesRef = collection(db, "pl_planilla_detalle");
    const q = query(
      detallesRef,
      and(
        where("ruc_empresa", "==", usuario.ruc),
        where("empleado_id", "==", empleadoId),
        where("periodo", "in", periodosAnteriores)
      )
    );

    const snapshot = await getDocs(q);
    let ingresosAcumulados = 0;
    let retencionesAcumuladas = 0;

    snapshot.forEach((doc) => {
      const detalle = doc.data();
      ingresosAcumulados += detalle.total_ingresos_afectos || 0;
      retencionesAcumuladas += detalle.descuentos?.renta_5ta || 0;
    });

    return { ingresosAcumulados, retencionesAcumuladas };
  } catch (error) {
    console.error("Error al obtener acumulados de Renta de 5ta:", error);
    return { ingresosAcumulados: 0, retencionesAcumuladas: 0 };
  }
};

export const mostrarError = (mensaje) => {
  const Swal = require("sweetalert2");
  Swal.fire({
    icon: "error",
    title: "Error",
    text: mensaje,
  });
};

export const mostrarAlerta = (titulo, mensaje, tipo) => {
  const Swal = require("sweetalert2");
  Swal.fire({
    icon: tipo,
    title: titulo,
    text: mensaje,
  });
};

export const exportarAExcel = (resultados, tipoPlanilla, config = {}) => {
  const XLSX = require("xlsx");
  const saludLabel = (config.tipo_seguro_micro === 'SIS' || (resultados.detalle && resultados.detalle.some(d => d.aportaciones?.essalud === 15))) ? 'Salud (SIS)' : 'EsSalud (9%)';
  
  const data = resultados.detalle.map((detalle) => ({
    Empleado: detalle.nombres,
    "Sueldo Base": detalle.sueldo_base,
    "Asignación Familiar": detalle.asignacion_familiar,
    ...(tipoPlanilla === "GRATIFICACION" && {
      Gratificación: detalle.gratificacion,
      "Bonificación Extraordinaria": detalle.bonificacion_extraordinaria,
      "Meses Computables": detalle.meses_computables,
    }),
    ...(tipoPlanilla === "CTS" && {
      CTS: detalle.cts,
      "Meses Computables": detalle.meses_computables,
    }),
    ...(tipoPlanilla === "MENSUAL" && {
      Vacaciones: detalle.vacaciones,
      "Bonificaciones Construcción Civil":
        detalle.bonificaciones_construccion_civil,
    }),
    "Total Ingresos": detalle.total_ingresos,
    "Fondo AFP": detalle?.descuentos?.sistema_pension?.fondo || 0,
    "Comisión AFP": detalle?.descuentos?.sistema_pension?.comision || 0,
    "Seguro AFP": detalle?.descuentos?.sistema_pension?.seguro || 0,
    "ONP": detalle?.descuentos?.sistema_pension?.onp || 0,
    "Renta 5ta": detalle?.descuentos?.renta_5ta || 0,
    "Descuento Faltas": detalle?.descuentos?.descuento_faltas || 0,
    "Descuento DSO": detalle?.descuentos?.descuento_dso || 0,
    "Descuento Permisos S/Goce": detalle?.descuentos?.descuento_permisos_sin_goce || 0,
    "Descuento Tardanzas": detalle?.descuentos?.descuento_tardanzas || 0,
    "Adelantos": detalle?.descuentos?.adelantos || 0,
    "Total Descuentos": detalle.total_descuentos,
    [saludLabel]: detalle?.aportaciones?.sis || detalle?.aportaciones?.essalud || 0,
    "EsSalud Vida": detalle?.aportaciones?.essalud_vida || 0,
    SCTR: detalle?.aportaciones?.sctr || 0,
    "Vida Ley": detalle?.aportaciones?.vida_ley || 0,
    "Total Aportaciones": detalle?.aportaciones?.total || 0,
    "Neto a Pagar": detalle.neto_pagar,
  }));

  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Planilla");
  XLSX.writeFile(wb, `planilla_${resultados.periodo}_${tipoPlanilla}.xlsx`);
};

const MESES_GRATIFICACION = [7, 12];
const MESES_CTS = [5, 11];
const PORCENTAJE_BONIFICACION_EXTRAORDINARIA = 0.09;


export const calcularBonos = (empleado, bonosMap) => {

  let bonos = {};
  let totalBonosRemunerativos = 0;
  let bonosRemunerativos = 0;
  let bonosNoRemunerativos = 0;

  if (empleado && empleado.bonos_asignados && Array.isArray(empleado.bonos_asignados)) {

    empleado.bonos_asignados.forEach((bonoAsignado, index) => {
      const bonoInfo = bonosMap?.[bonoAsignado.bonoId];

      const nombreBono =
        bonoInfo?.tipo_bono ||
        bonoInfo?.nombre ||
        "Bono desconocido";

      const monto = Number(bonoAsignado.monto || 0);

      bonos[nombreBono] = monto;

      const esRemunerativo =
        bonoInfo?.remunerativo ?? bonoAsignado?.remunerativo ?? false;

      if (esRemunerativo) {
        bonosRemunerativos += monto;
        totalBonosRemunerativos += monto;

      } else {
        bonosNoRemunerativos += monto;

      }

    });
  } else {
    console.log("El empleado no tiene bonos_asignados válidos");
  }

  return {
    bonos,
    totalBonosRemunerativos,
    bonosRemunerativos,
    bonosNoRemunerativos,
  };
};


export const construirDetalleEmpleado = (
  empleadoId,
  empleado,
  sueldoBaseOriginal,
  asignacionFamiliar,
  bonos,
  regimenAplicar,
  descuentosFaltas,
  descuentosTardanzas,
  datosAsistencia,
  totalIngresos,
  descuentos,
  aportaciones,
  diasTrabajados,
  diasDescanso,
  horasExtras = { monto25: 0, monto35: 0, monto100: 0, total: 0 },
  gratificacion = 0,
  cts = 0,
  vacaciones = 0,
  bonificacionExtraordinaria = 0,
  bonificacionesConstruccionCivil = 0,
  mesesComputables = null,
  totalIngresosBruto = 0,
  totalIngresosAfectos = 0,
  adelantos = [],
  provisiones = { vacaciones: 0, gratificaciones: 0, cts: 0, total: 0 },
  diasVacaciones = 0,
  diasDescansoMedico = 0
) => {
  const result = {
    empleado_id: empleadoId,
    nombres: `${empleado?.nombres || ""} ${empleado?.apellidos || ""}`,
    departamento: empleado?.departamento || "Sin Departamento",
    area: empleado?.area || "Sin Área",
    tipo_contrato: empleado?.tipo_contrato || "Sin Especificar",
    dias_trabajados: diasTrabajados,
    dias_descanso: diasDescanso,
  sueldo_base: sueldoBaseOriginal,
  asignacion_familiar: asignacionFamiliar,
  bonos,
  regimen_pensionario: empleado?.regimen_pensionario || "NO ASIGNADO",
  regimen_aplicado: regimenAplicar,
  ingresos: {
    horas_extras_25: horasExtras.monto25 || 0,
    horas_extras_35: horasExtras.monto35 || 0,
    horas_extras_100: horasExtras.monto100 || 0,
  },
  gratificacion,
  cts,
  vacaciones,
  bonificacion_extraordinaria: bonificacionExtraordinaria,
  bonificaciones_construccion_civil: bonificacionesConstruccionCivil,
  ...(mesesComputables !== null && { meses_computables: mesesComputables }),
  dias_faltantes: descuentosFaltas?.dias_descontados || 0,
  dias_tardanzas: descuentosTardanzas?.dias_con_tardanza || 0,
  minutos_tardanza_total: descuentosTardanzas?.minutos_totales || 0,
  faltas_injustificadas: datosAsistencia?.faltas?.injustificadas || 0,
  faltas_justificadas: datosAsistencia?.faltas?.justificadas || 0,
  permisos_sin_goce: datosAsistencia?.permisos?.sin_goce || 0,
  permisos_con_goce: datosAsistencia?.permisos?.con_goce || 0,
  
  // Valorización de ausentismo para reportes
  monto_faltas: descuentos?.descuento_faltas || descuentosFaltas?.monto_descuento || descuentosFaltas?.total || 0,
  monto_permisos_sin_goce: descuentos?.descuento_permisos_sin_goce || 0,
  monto_permisos_con_goce: Number(((sueldoBaseOriginal + asignacionFamiliar) / 30 * (datosAsistencia?.permisos?.con_goce || 0)).toFixed(2)),
  
  dias_vacaciones: diasVacaciones,
  dias_descanso_medico: diasDescansoMedico,
  he_25: datosAsistencia?.horas_extras?.horas25 || 0,
  he_35: datosAsistencia?.horas_extras?.horas35 || 0,
  he_100: datosAsistencia?.horas_extras?.horas100 || 0,
  total_ingresos: totalIngresos,
  total_ingresos_bruto: totalIngresosBruto,
  total_ingresos_afectos: totalIngresosAfectos,
  descuentos: {
    ...descuentos,
    sistema_pension: {
      fondo: descuentos?.sistema_pension?.fondo ?? 0,
      comision: descuentos?.sistema_pension?.comision ?? 0,
      seguro: descuentos?.sistema_pension?.seguro ?? 0,
      onp: descuentos?.sistema_pension?.onp ?? 0,
      total: descuentos?.sistema_pension?.total ?? 0,
    },
  },
  aportaciones,
  total_descuentos: descuentos.total,
  total_aportaciones: aportaciones.total,
  adelantos: adelantos, // Lista de adelantos aplicados
  provisiones,
    neto_pagar: Number((totalIngresosBruto - descuentos.total).toFixed(2)),
    costo_laboral_total: Number((totalIngresosBruto + (aportaciones.total || 0) + (provisiones.total || 0)).toFixed(2)),
  };
  
  console.log(`🏗️ [construirDetalleEmpleado] Resultado para ${result.nombres}:`, {
    total_descuentos: result.total_descuentos,
    adelantos_monto: result.descuentos?.adelantos,
    adelantos_array_length: result.adelantos?.length
  });

  return result;
};

export const calcularPlanillaMensual = async (
  empleado,
  empleadoId,
  periodoCalculo,
  regimenAplicar,
  configuracionRegimen,
  datosAsistencia,
  config,
  bonosMap,
  calcularDescuentosPorFaltas,
  calcularDescuentosPorTardanzas,
  calcularEssalud,
  calcularRenta5ta,
  calcularSistemaPensionario,
  calcularEssaludVida,
  calcularSCTR,
  calcularVidaLey,
  calcularBonificacionesConstruccionCivil,
  usuario,
  ajustes = {},
  historial = []
) => {
  const sueldoBaseOriginal = Number(empleado?.sueldo_base || 0);

  const descuentosFaltas = calcularDescuentosPorFaltas(
    empleado,
    datosAsistencia.faltas,
    datosAsistencia.permisos,
    config,
    {
      diasTrabajo: Number(datosAsistencia?.resumen_rol?.dias_trabajo_ciclo || 0),
      diasDescanso: Number(datosAsistencia?.resumen_rol?.dias_descanso_ciclo || 0),
    },
    regimenAplicar
  );
  const descuentosTardanzas = calcularDescuentosPorTardanzas(
    empleado,
    datosAsistencia.tardanzas,
    config
  );

  // --- CÁLCULO DE DÍAS DEL PERIODO ---
  const [anioP, mesP] = periodoCalculo.fechaInicio.split("-").map(Number);
  const diasMes = new Date(anioP, mesP, 0).getDate();
  let diasCalendarioTrabajados = 0;
  let diasCalendarioDescanso = 0;
  const resumenRol = datosAsistencia?.resumen_rol;

  if (
    resumenRol &&
    Number.isFinite(Number(resumenRol.dias_trabajo_programados)) &&
    Number.isFinite(Number(resumenRol.dias_descanso_programados))
  ) {
    diasCalendarioTrabajados = Number(resumenRol.dias_trabajo_programados);
    diasCalendarioDescanso = Number(resumenRol.dias_descanso_programados);
  } else {
    for (let d = 1; d <= diasMes; d++) {
      const fecha = new Date(anioP, mesP - 1, d);
      const diaSem = fecha.getDay();
      if (diaSem >= 1 && diaSem <= 5) diasCalendarioTrabajados++; // Lun-Vie
      else diasCalendarioDescanso++; // Sab-Dom
    }
  }
  // --- FIN CÁLCULO DÍAS ---

  let vacaciones = 0;
  let diasVacacionesCount = 0;
  let sueldoAjustadoPorVacaciones = sueldoBaseOriginal;

  if (datosAsistencia.asistencias && Array.isArray(datosAsistencia.asistencias)) {
    diasVacacionesCount = datosAsistencia.asistencias.reduce((sum, occ) => {
      // Contar si está activo o es de los estados que permitimos procesar aunque no estén aprobados explícitamente en UI
      const esValido = occ.activo || ["detectado_automatico", "manual_importado", "manual"].includes(occ.estado);
      if (esValido && occ.tipo === "vacaciones") {
        return sum + (Number(occ.horas_afectadas || 0) / 8);
      }
      return sum;
    }, 0);

    if (diasVacacionesCount > 0) {
      vacaciones = Number((sueldoBaseOriginal / 30 * diasVacacionesCount).toFixed(2));
      sueldoAjustadoPorVacaciones = Number((sueldoBaseOriginal - vacaciones).toFixed(2));
      // Restar días de vacaciones de los días trabajados físicamente
      diasCalendarioTrabajados = Math.max(0, diasCalendarioTrabajados - diasVacacionesCount);
    }
  }

  // Restar faltas injustificadas de los días trabajados
  const diasFaltas = descuentosFaltas.dias_descontados || 0;
  const diasTrabajadosFinal = Math.max(0, diasCalendarioTrabajados - diasFaltas);

  const sueldoBase = Number((sueldoAjustadoPorVacaciones - descuentosFaltas.monto_descuento).toFixed(2));

  const asignacionFamiliar =
    empleado.asignacion_familiar === true &&
      configuracionRegimen.parametros[regimenAplicar]?.asignacion_familiar?.aplica
      ? Number(config.asignacion_familiar)
      : 0;

  // CÁLCULO DE HORAS EXTRAS
  // Filtrar ocurrencias de HE desde datosAsistencia
  // datosAsistencia.asistencias contiene todas, pero aqui ya las tenemos clasificadas?
  // datosAsistencia suele venir de obtenerDatosAsistencia que devuelve { faltas, tardanzas, permisos, asistencias: all_ocurrencias }
  // Asumiremos que datosAsistencia.asistencias tiene todas las del periodo.

  const reporteHE = { horas25: 0, horas35: 0, horas100: 0 };

  if (datosAsistencia.asistencias && Array.isArray(datosAsistencia.asistencias)) {
    datosAsistencia.asistencias.forEach(occ => {
      // Contar si está activo o es de los estados permitidos
      const esValido = occ.activo || ["detectado_automatico", "manual_importado", "manual"].includes(occ.estado);
      if (esValido) {
        if (occ.tipo === "he_25") reporteHE.horas25 += Number(occ.horas_afectadas || 0);
        if (occ.tipo === "he_35") reporteHE.horas35 += Number(occ.horas_afectadas || 0);
        if (occ.tipo === "he_100") reporteHE.horas100 += Number(occ.horas_afectadas || 0);
      }
    });
  }

  const horasExtras = calcularHorasExtras(
    sueldoBaseOriginal + asignacionFamiliar, // Base cálculo incluye AF
    reporteHE.horas25,
    reporteHE.horas35,
    reporteHE.horas100
  );

  const {
    bonos,
    totalBonosRemunerativos,
    bonosNoRemunerativos,
    bonosRemunerativos,
  } = calcularBonos(empleado, bonosMap);

  const bonosAfectos = Number(
    bonosRemunerativos ?? totalBonosRemunerativos ?? 0
  );

  // No content here, moved up

  // NUEVO: Procesar Ajustes Manuales del Contador
  const montoAjustesIngresosAfectos = (ajustes.ingresos || [])
    .filter(a => a.afecto)
    .reduce((sum, a) => sum + Number(a.monto || 0), 0);

  const montoAjustesIngresosInafectos = (ajustes.ingresos || [])
    .filter(a => !a.afecto)
    .reduce((sum, a) => sum + Number(a.monto || 0), 0);

  const montoAjustesDescuentos = (ajustes.descuentos || [])
    .reduce((sum, a) => sum + Number(a.monto || 0), 0);

  let bonificacionesConstruccionCivil = 0;
  if (regimenAplicar === "CONSTRUCCION_CIVIL") {
    bonificacionesConstruccionCivil = calcularBonificacionesConstruccionCivil(
      empleado,
      periodoCalculo,
      config
    );
  }

  const totalIngresosBruto = Number(
    (
      sueldoAjustadoPorVacaciones +
      asignacionFamiliar +
      bonosAfectos +
      (bonosNoRemunerativos || 0) +
      vacaciones +
      bonificacionesConstruccionCivil +
      horasExtras.total +
      montoAjustesIngresosAfectos +
      montoAjustesIngresosInafectos
    ).toFixed(2)
  );

  const totalIngresosAfectosBase = Number(
    (
      sueldoBase +
      asignacionFamiliar +
      bonosAfectos +
      vacaciones +
      bonificacionesConstruccionCivil +
      horasExtras.total +
      montoAjustesIngresosAfectos
    ).toFixed(2)
  );

  // El permiso sin goce reduce ingresos afectos (base pensionaria/tributaria) y además se refleja en descuentos.
  const descuentoPermisosSinGoce = Number(
    descuentosFaltas?.monto_descuento_permisos_sin_goce || 0
  );
  const totalIngresosAfectos = Number(
    Math.max(0, totalIngresosAfectosBase - descuentoPermisosSinGoce).toFixed(2)
  );

  let totalIngresos = totalIngresosAfectos;
  totalIngresos = Number(totalIngresos.toFixed(2));

  const sistemaPension = calcularSistemaPensionario(
    totalIngresosAfectos,
    empleado,
    config
  );
  const descuentosSistemaPension = sistemaPension.total || 0;

  const essalud = calcularEssalud(totalIngresosAfectos, {
    codigo: regimenAplicar,
    essalud: configuracionRegimen.parametros[regimenAplicar]?.essalud || { porcentaje: 9 },
    rmv: config.asignacion_familiar / 0.1,
  }, config);

  const mesActualRenta = Number(periodoCalculo.fechaInicio.split("-")[1]);
  const { retencionesAcumuladas, ingresosAcumulados } = await obtenerAcumuladosRenta5ta(
    empleadoId,
    periodoCalculo.fechaInicio,
    usuario
  );

  let numGratificacionesProyectadas = 0;
  // Para cálculo de quinta categoría, siempre proyectar todas las gratificaciones del año
  // independientemente del mes actual, ya que es un cálculo anual
  console.log('🔍 Debug configuracionRegimen:', {
    configuracionRegimen,
    regimenAplicar,
    parametrosRegimen: configuracionRegimen?.parametros?.[regimenAplicar]
  });
  // Verificar si aplica gratificación según régimen (con fallback para GENERAL)
  const aplicaGratificacion = (configuracionRegimen?.parametros?.[regimenAplicar]?.gratificacion?.aplica !== false) && (regimenAplicar !== "MYPE_MICRO");
  if (aplicaGratificacion) {
    // En Perú hay 2 gratificaciones al año: julio (enero-junio) y diciembre (julio-diciembre)
    // Para proyección anual, siempre considerar ambas
    // EXCEPCIÓN: Si el factor es 0 o aplica es false, numGratificacionesProyectadas será 0 (manejado por aplicaGratificacion)
    numGratificacionesProyectadas = 2;
  }

  let gratificacionesProyectadas = 0;
  
  // NUEVO: Calcular base computable respetando el Principio de Regularidad (3/6)
  // para que bonos esporádicos no inflen las provisiones.
  const bonosRegulares = calcularBonosRemunerativos(empleado, historial || [], bonosMap || {});
  const baseComputableProvisiones = Number(
    (
      sueldoBase +
      asignacionFamiliar +
      bonosRegulares +
      horasExtras.total
    ).toFixed(2)
  );

  let baseGratificacion = baseComputableProvisiones; 
  let factorGratificacion = configuracionRegimen?.parametros?.[regimenAplicar]?.gratificacion?.factor ?? 1; // Definir fuera del if

  if (
    aplicaGratificacion &&
    numGratificacionesProyectadas > 0
  ) {
    // CORRECCIÓN: Usar baseComputableProvisiones (con bonos regulares) como base para la gratificación
    baseGratificacion = baseComputableProvisiones;

    factorGratificacion = configuracionRegimen?.parametros?.[regimenAplicar]?.gratificacion?.factor ?? 1;
    gratificacionesProyectadas =
      baseGratificacion *
      factorGratificacion *
      numGratificacionesProyectadas;

    console.log('🧮 Cálculo de gratificaciones proyectadas:', {
      regimenAplicar,
      numGratificacionesProyectadas,
      baseGratificacion,
      factor: configuracionRegimen.parametros[regimenAplicar]?.gratificacion?.factor,
      gratificacionesProyectadas
    });
  } else {
    console.log('⚠️ No se calculan gratificaciones proyectadas:', {
      aplica: configuracionRegimen.parametros[regimenAplicar]?.gratificacion?.aplica,
      numGratificacionesProyectadas,
      regimenAplicar
    });
  }

  // Calcular provisiones mensuales
  const diasVacaciones = configuracionRegimen.parametros[regimenAplicar]?.vacaciones?.dias || 30;

  // CORRECCIÓN A: Base Provisiones debe incluir Asignación Familiar, Bonos REGULARES y Horas Extras
  // Usamos baseComputableProvisiones que aplica la regla de frecuencia 3/6
  const baseCalculoProvisiones = baseComputableProvisiones;

  const baseCalculoVacaciones = baseCalculoProvisiones;
  const provisionVacaciones = calcularProvisiónVacaciones(baseCalculoVacaciones, diasVacaciones);

  const provisionGratificaciones = gratificacionesProyectadas > 0
    ? calcularProvisiónGratificaciones(baseGratificacion, numGratificacionesProyectadas, factorGratificacion)
    : 0;

  // CORRECCIÓN B: Fórmula CTS = (Sueldo + AsigFam + (1/6 Grati)) / 12
  // En Pequeña Empresa REMYPE no se incluye 1/6 de gratificación en la base de CTS
  const esMYPEPequena = regimenAplicar === "MYPE_PEQUENA" || regimenAplicar === "PEQUENA_EMPRESA";
  const unSextoGratificacion = (aplicaGratificacion && !esMYPEPequena) ? (baseGratificacion / 6) : 0;
  const baseCalculoCTS = baseCalculoVacaciones + unSextoGratificacion; // Base CTS = Remuneración + 1/6 Grati (si aplica)

  const configCTS = configuracionRegimen.parametros[regimenAplicar]?.cts;
  console.log('🔍 Debug CTS Config:', {
    regimen: regimenAplicar,
    configCTS,
    aplica: configCTS?.aplica,
    baseCalculoCTS,
    unSextoGratificacion,
    esMYPEPequena
  });

  // Provisión mensual = (Base CTS * Factor) / 12
  // Si configCTS.aplica es undefined, asumimos true (para GENERAL y otros que lo tengan definido)
  // Solo si es explícitamente false lo inhabilitamos.
  const aplicaCTS = configCTS && configCTS.aplica !== false;
  const factorCTS = configCTS?.factor ?? 1;

  const provisionCTS = aplicaCTS
    ? (baseCalculoCTS * factorCTS) / 12
    : 0;

  // NUEVO: Cargas Sociales sobre Provisiones
  const provisionBonoExtraordinario = provisionGratificaciones * 0.09;

  // Si es SIS en Microempresa, la provisión de salud sobre vacaciones es (Costo SIS / 2) / 12 meses
  // ya que solo tienen 15 días de vacaciones al año.
  const esSISMicro = regimenAplicar === "MYPE_MICRO" && config?.tipo_seguro_micro === "SIS";
  const provisionEsSaludVacaciones = esSISMicro
    ? ((Number(config?.costo_sis) || 15) / 2) / 12
    : provisionVacaciones * 0.09;

  // Consolidación Costo Empresa
  const totalProvisiones = provisionVacaciones + provisionGratificaciones + provisionCTS + provisionBonoExtraordinario + provisionEsSaludVacaciones;

  console.log('📊 Cálculo de provisiones mensuales:', {
    regimenAplicar,
    aplicaGratificacion,
    numGratificacionesProyectadas,
    baseGratificacion,
    factorGratificacion,
    gratificacionesProyectadas,
    provisionVacaciones,
    provisionEsSaludVacaciones,
    provisionGratificaciones,
    provisionBonoExtraordinario,
    provisionCTS,
    totalProvisiones
  });

  // Para quinta categoría, usar base imponible ajustada por incidencias
  // Regla de Ajuste:
  // 1. Mes Actual: Usar totalIngresosAfectos (ya tiene descontadas faltas y tardanzas)
  // 2. Meses Futuros: Proyectar con sueldo base completo (sueldoBaseOriginal + AsigFam + BonosAfectos)

  const ingresoMesActual = totalIngresosAfectos; // S/ 5824 (ejemplo con descuentos)

  const ingresoProyectadoFuturo = Number(
    (
      sueldoBaseOriginal +
      asignacionFamiliar +
      bonosAfectos
    ).toFixed(2)
  );

  const mesesFaltantes = 12 - mesActualRenta;

  // Ingresos Futuros Proyectados (sin descuentos, asumiendo asistencia perfecta)
  const ingresosFuturos = ingresoProyectadoFuturo * mesesFaltantes;



  // El divisor para la retención debe ser el número de meses faltantes INCLUYENDO el actual
  const mesesFaltantesDivisor = 12 - mesActualRenta + 1;

  // Base Imponible Anual Revisada
  // Ingresos Acumulados (Ene-Sep) + Ingreso Actual (Oct con faltas) + Futuros (Nov-Dic completos) + Gratificaciones
  const ingresoAnualManual = ingresosAcumulados + ingresoMesActual + ingresosFuturos + gratificacionesProyectadas;

  console.log('📉 Proyección Renta 5ta Avanzada:', {
    ingresoMesActual,
    mesesFaltantesDivisor,
    ingresosFuturos,
    ingresoAnualManual
  });

  const renta5taCalculada = calcularRenta5ta(
    ingresoMesActual,
    mesesFaltantesDivisor,
    gratificacionesProyectadas,
    retencionesAcumuladas,
    ingresosAcumulados,
    config,
    ingresoAnualManual
  );

  // NUEVO: Sobreescritura de Renta 5ta por Ajuste Manual del Contador
  const renta5ta = ajustes.renta_5ta_override !== undefined 
    ? Number(ajustes.renta_5ta_override) 
    : renta5taCalculada;

  const essaludVida = calcularEssaludVida(empleado, config);
  const sctrData = calcularSCTR(totalIngresosAfectos, empleado, config);
  const sctr = sctrData.total;
  const vidaLey = calcularVidaLey(empleado);

  // 6. Adelantos
  const adelantos = await obtenerAdelantosPendientes(
    usuario.ruc,
    empleadoId,
    periodoCalculo.fechaInicio.split("-").slice(0, 2).join("-")
  );
  const totalAdelantos = calcularTotalAdelantos(adelantos);
  console.log(`💰 [calcularPlanillaMensual] Empleado: ${empleado.nombres}, Adelantos: ${totalAdelantos} (${adelantos.length} items)`);

  const totalDescuentos = Number(
    (
      descuentosSistemaPension +
      renta5ta +
      descuentosFaltas.monto_descuento +
      descuentosFaltas.monto_dso +
      descuentosTardanzas.monto_descuento +
      descuentoPermisosSinGoce +
      totalAdelantos +
      montoAjustesDescuentos
    ).toFixed(2)
  );

  // NUEVO: Ajuste de Redondeo Final (Céntimos)
  const redondeoCentimos = Number(ajustes.redondeo_centimos || 0);
  const totalDescuentosFinal = Number((totalDescuentos + redondeoCentimos).toFixed(2));

  const totalAportaciones = Number((essalud + sctr + vidaLey + essaludVida).toFixed(2));

  const descuentos = {
    sistema_pension: sistemaPension,
    renta_5ta: renta5ta,
    essalud_vida: essaludVida,
    descuento_faltas: descuentosFaltas.monto_descuento,
    descuento_dso: descuentosFaltas.monto_dso,
    descuento_tardanzas: descuentosTardanzas.monto_descuento,
    descuento_permisos_sin_goce: descuentoPermisosSinGoce,
    adelantos: totalAdelantos,
    total: totalDescuentos,
  };

  const aportaciones = {};
  if (config.tipo_seguro_micro === 'SIS') {
    aportaciones.sis = essalud;
  } else {
    aportaciones.essalud = essalud;
  }
  
  Object.assign(aportaciones, {
    sctr,
    sctr_pension: sctrData.pension,
    sctr_salud: sctrData.salud,
    vida_ley: vidaLey,
    essalud_vida: essaludVida,
    total: totalAportaciones,
  });

  const result = construirDetalleEmpleado(
    empleadoId,
    empleado,
    sueldoAjustadoPorVacaciones,
    asignacionFamiliar,
    bonos,
    regimenAplicar,
    descuentosFaltas,
    descuentosTardanzas,
    {
      ...datosAsistencia,
      horas_extras: reporteHE, // Guardamos las horas
      detalle_he: horasExtras // Guardamos los montos
    },
    totalIngresos,
    descuentos,
    aportaciones,
    diasTrabajadosFinal, // Usar variable calculada
    diasCalendarioDescanso, // Usar variable calculada
    horasExtras,
    0,
    0,
    vacaciones,
    0,
    bonificacionesConstruccionCivil,
    null,
    totalIngresosBruto,
    totalIngresosAfectos,
    adelantos, // <-- Agregar adelantos
    {
      vacaciones: provisionVacaciones,
      essalud_vacaciones: provisionEsSaludVacaciones,
      gratificaciones: provisionGratificaciones,
      cts: provisionCTS,
      bono_extraordinario: provisionBonoExtraordinario,
      total: totalProvisiones,
    },
    diasVacacionesCount || 0,
    datosAsistencia.dias_descanso_medico || 0
  );

  // Inyectar los ajustes en el objeto final para trazabilidad
  result.ajustes = ajustes;
  result.total_descuentos = totalDescuentosFinal;
  result.neto_pagar = Number((totalIngresosBruto - totalDescuentosFinal).toFixed(2));

  return result;
}

export const calcularPlanillaGratificacion = async (
  empleado,
  empleadoId,
  periodo,
  ultimoDia,
  regimenAplicar,
  configuracionRegimen,
  datosAsistencia,
  config,
  bonosMap,
  calcularMesesComputables,
  calcularGratificacion,
  calcularSistemaPensionario,
  calcularRenta5ta,
  mostrarAlerta,
  usuario // H005: necesario para obtener acumulados de renta 5ta
) => {
  const mes = parseInt(periodo.split("-")[1], 10);
  const anio = parseInt(periodo.split("-")[0], 10);
  if (!MESES_GRATIFICACION.includes(mes)) {
    mostrarAlerta(
      "Información",
      "Las gratificaciones normalmente se pagan en julio y diciembre. Este cálculo es solo estimativo.",
      "info"
    );
  }
  let periodoCalculo;
  if (mes === 7)
    periodoCalculo = {
      fechaInicio: `${anio}-01-01`,
      fechaFin: `${anio}-06-30`,
    };
  else if (mes === 12)
    periodoCalculo = {
      fechaInicio: `${anio}-07-01`,
      fechaFin: `${anio}-12-31`,
    };
  else
    periodoCalculo = {
      fechaInicio: `${periodo}-01`,
      fechaFin: `${periodo}-${ultimoDia}`,
    };
  const fechaEfectiva = empleado.fecha_reingreso || empleado.fecha_ingreso;
  const mesesComputables = calcularMesesComputables(
    fechaEfectiva,
    periodoCalculo
  );
  const gratificacion = await calcularGratificacion(
    empleado,
    {
      codigo: regimenAplicar,
      gratificacion:
        configuracionRegimen.parametros[regimenAplicar]?.gratificacion,
      asignacion_familiar:
        configuracionRegimen.parametros[regimenAplicar]?.asignacion_familiar,
    },
    periodoCalculo,
    { rmv: config.sueldo_minimo } // RMV correcto: sueldo mínimo
  );
  const bonificacionExtraordinaria = Number(
    (gratificacion * PORCENTAJE_BONIFICACION_EXTRAORDINARIA).toFixed(2)
  );

  const sueldoBaseOriginal = 0;
  const asignacionFamiliar = 0;

  const totalIngresos = gratificacion + bonificacionExtraordinaria;

  // Calcular descuentos sobre la gratificación
  const sistemaPension = calcularSistemaPensionario(
    totalIngresos,
    empleado,
    config
  );
  const descuentosSistemaPension = sistemaPension.total || 0;

  // H005: Calcular quinta categoría sobre la gratificación integrándola
  // al cálculo anual del trabajador (no como ingreso aislado)
  let renta5ta = 0;
  if (usuario) {
    // 1. Obtener ingresos y retenciones reales acumulados del año
    const { ingresosAcumulados, retencionesAcumuladas } = await obtenerAcumuladosRenta5ta(
      empleadoId,
      periodo,
      usuario
    );

    // 2. Determinar meses restantes (incluye mes actual)
    const mesesFaltantes = 12 - mes + 1;

    // 3. Proyectar ingresos futuros (sueldo mensual × meses restantes después de la gratificación)
    const sueldoBase = Number(empleado.sueldo_base || 0);
    const asignacionFam = empleado.asignacion_familiar === true &&
      configuracionRegimen.parametros[regimenAplicar]?.asignacion_familiar?.aplica
      ? Number(config.asignacion_familiar || 0) : 0;
    const ingresoProyectadoFuturo = (sueldoBase + asignacionFam) * Math.max(0, mesesFaltantes - 1);

    // 4. Proyectar gratificaciones futuras (diciembre si estamos en julio, 0 si estamos en diciembre)
    let gratificacionesFuturas = 0;
    if (mes <= 7) {
      // Julio: falta la gratificación de diciembre
      const baseGrati = sueldoBase + asignacionFam;
      const factorGrati = configuracionRegimen.parametros[regimenAplicar]?.gratificacion?.factor ?? 1;
      gratificacionesFuturas = baseGrati * factorGrati;
    }
    // Diciembre: no faltan gratificaciones

    // 5. Construir ingreso anual manual completo
    const ingresoAnualManual = ingresosAcumulados + totalIngresos + ingresoProyectadoFuturo + gratificacionesFuturas;

    console.log('🧮 H005 - Renta 5ta sobre gratificación:', {
      ingresosAcumulados,
      retencionesAcumuladas,
      totalIngresosGratificacion: totalIngresos,
      ingresoProyectadoFuturo,
      gratificacionesFuturas,
      ingresoAnualManual,
      mesesFaltantes
    });

    // 6. Calcular retención usando la proyección anual completa
    renta5ta = calcularRenta5ta(
      totalIngresos,      // ingreso del mes (gratificación + 9%)
      mesesFaltantes,      // meses restantes incluyendo actual
      0,                   // gratificaciones ya incluidas en ingresoAnualManual
      retencionesAcumuladas, // retenciones reales de meses anteriores
      0,                   // ingresos ya incluidos en ingresoAnualManual
      config,
      ingresoAnualManual   // proyección anual completa
    );
  } else {
    // Fallback si no hay usuario (no debería ocurrir en producción)
    console.warn('⚠️ calcularPlanillaGratificacion: usuario no disponible, renta 5ta = 0');
    renta5ta = 0;
  }

  const descuentosFaltas = { monto_descuento: 0, dias_descontados: 0 };
  const descuentosTardanzas = {
    monto_descuento: 0,
    dias_con_tardanza: 0,
    minutos_totales: 0,
  };
  const { bonos } = calcularBonos(empleado, bonosMap);

  const descuentos = {
    sistema_pension: sistemaPension,
    renta_5ta: renta5ta,
    essalud_vida: 0,
    descuento_faltas: descuentosFaltas.monto_descuento,
    descuento_dso: 0,
    descuento_tardanzas: descuentosTardanzas.monto_descuento,
    total: descuentosSistemaPension + renta5ta + descuentosFaltas.monto_descuento + descuentosTardanzas.monto_descuento,
  };
  const aportaciones = { essalud: 0, sctr: 0, vida_ley: 0, total: 0 };

  return construirDetalleEmpleado(
    empleadoId,
    empleado,
    sueldoBaseOriginal,
    asignacionFamiliar,
    bonos,
    regimenAplicar,
    descuentosFaltas,
    descuentosTardanzas,
    datosAsistencia,
    totalIngresos, // Se pasa el total correcto
    descuentos,
    aportaciones,
    gratificacion, // Se pasa el monto de gratificación calculado
    0, // cts
    0, // vacaciones
    bonificacionExtraordinaria, // Se pasa la bonificación calculada
    0, // bonificacionesConstruccionCivil
    mesesComputables, // Se pasa mesesComputables en la posición correcta
    totalIngresos, // totalIngresosBruto
    gratificacion // totalIngresosAfectos (solo la grati es afecta para otros cálculos)
  );
};

export const calcularPlanillaCTS = async (
  empleado,
  empleadoId,
  periodo,
  ultimoDia,
  regimenAplicar,
  configuracionRegimen,
  datosAsistencia,
  config,
  bonosMap,
  calcularMesesComputables,
  calcularCTS,
  mostrarAlerta,
  usuario
) => {
  const mes = parseInt(periodo.split("-")[1], 10);
  const anio = parseInt(periodo.split("-")[0], 10);

  // H004: Obtener la última gratificación computable para incluir 1/6 en base CTS
  // CTS mayo → busca gratificación diciembre año anterior
  // CTS noviembre → busca gratificación julio mismo año
  let ultimaGratificacionReal;
  try {
    ultimaGratificacionReal = await obtenerUltimaGratificacion(empleadoId, periodo, usuario);
    if (ultimaGratificacionReal > 0) {
      console.log("🔍 calcularPlanillaCTS - Gratificación encontrada:", ultimaGratificacionReal,
        "→ 1/6 =", Number((ultimaGratificacionReal / 6).toFixed(2)));
    } else {
      console.log("🔍 calcularPlanillaCTS - Sin gratificación anterior computable");
    }
  } catch (error) {
    // NO convertir silenciosamente un error de infraestructura en gratificación = 0
    console.error("❌ ERROR obteniendo última gratificación para CTS:", error);
    throw new Error(
      `No se pudo obtener la gratificación anterior para el empleado ${empleadoId} ` +
      `(periodo CTS: ${periodo}). Error: ${error.message}. ` +
      `La CTS no puede calcularse sin verificar la gratificación computable.`
    );
  }

  let periodoCalculo;
  if (mes === 5) {
    // CTS de Mayo: se calcula de Noviembre (año anterior) a Abril.
    periodoCalculo = {
      fechaInicio: `${anio - 1}-11-01`,
      fechaFin: `${anio}-04-30`,
    };
  } else if (mes === 11) {
    // CTS de Noviembre: se calcula de Mayo a Octubre.
    periodoCalculo = {
      fechaInicio: `${anio}-05-01`,
      fechaFin: `${anio}-10-31`,
    };
  } else {
    // Caso estimativo fuera de los meses oficiales.
    periodoCalculo = {
      fechaInicio: `${periodo}-01`,
      fechaFin: `${periodo}-${ultimoDia}`,
    };
  }

  console.log("🔍 calcularPlanillaCTS - Periodo de cálculo:", periodoCalculo);

  const fechaEfectiva = empleado.fecha_reingreso || empleado.fecha_ingreso;
  const mesesComputables = calcularMesesComputables(
    fechaEfectiva,
    periodoCalculo
  );
  console.log("🔍 calcularPlanillaCTS - Meses computables:", mesesComputables);

  const regimenConfig = {
    codigo: regimenAplicar,
    cts: configuracionRegimen.parametros[regimenAplicar]?.cts,
    gratificacion:
      configuracionRegimen.parametros[regimenAplicar]?.gratificacion,
    asignacion_familiar:
      configuracionRegimen.parametros[regimenAplicar]
        ?.asignacion_familiar,
  };

  console.log("🔍 calcularPlanillaCTS - Configuración del régimen completa:", configuracionRegimen);
  console.log("🔍 calcularPlanillaCTS - Configuración del régimen filtrada:", regimenConfig);

  const cts = Number(
    Math.max(
      0,
      await calcularCTS(
        empleado,
        regimenConfig,
        periodoCalculo,
        { rmv: config.sueldo_minimo }, // RMV correcto: sueldo mínimo
        ultimaGratificacionReal
      )
    ).toFixed(2)
  );

  console.log("🔍 calcularPlanillaCTS - Monto CTS calculado:", cts);

  const sueldoBaseOriginal = 0;
  const asignacionFamiliar = 0;
  const { bonos } = calcularBonos(empleado, bonosMap); // Se mantiene por si hay bonos especiales
  const descuentosFaltas = { monto_descuento: 0, dias_descontados: 0 };
  const descuentosTardanzas = {
    monto_descuento: 0,
    dias_con_tardanza: 0,
    minutos_totales: 0,
  };
  const descuentos = {
    sistema_pension: {},
    renta_5ta: 0,
    essalud_vida: 0,
    descuento_faltas: 0,
    descuento_dso: 0,
    descuento_tardanzas: 0,
    total: 0,
  };
  const aportaciones = { essalud: 0, sctr: 0, vida_ley: 0, total: 0 };

  const totalIngresos = cts;

  return construirDetalleEmpleado(
    empleadoId,
    empleado,
    sueldoBaseOriginal, // Se pasa 0
    asignacionFamiliar, // Se pasa 0
    bonos,
    regimenAplicar,
    descuentosFaltas,
    descuentosTardanzas,
    datosAsistencia,
    totalIngresos, // El ingreso es el monto de la CTS
    descuentos,
    aportaciones,
    0, // gratificacion
    cts, // cts
    0, // vacaciones
    0, // bonificacionExtraordinaria
    0, // bonificacionesConstruccionCivil
    mesesComputables,
    totalIngresos, // totalIngresosBruto es el mismo monto de la CTS
    0 // totalIngresosAfectos es 0, la CTS no está afecta a descuentos.
  );
};

// Función para obtener adelantos pendientes de un empleado en un período
export const obtenerAdelantosPendientes = async (ruc, empleadoId, periodo) => {
  try {
    console.log(`🔍 [obtenerAdelantosPendientes] Buscando para RUC: ${ruc}, Empleado: ${empleadoId}, Periodo: ${periodo}`);
    
    const { collection, query, where, getDocs, getFirestore } = await import(
      "firebase/firestore"
    );
    const firebaseApp = (await import("../firebase/credenciales")).default;
    const db = getFirestore(firebaseApp);

    // El periodo viene como YYYY-MM
    const [year, month] = periodo.split("-").map(Number);
    // Crear fechas de inicio y fin de mes considerando la zona horaria local
    const fechaInicio = new Date(year, month - 1, 1, 0, 0, 0);
    const fechaFin = new Date(year, month, 0, 23, 59, 59);

    console.log(`📅 [obtenerAdelantosPendientes] Rango de búsqueda: ${fechaInicio.toISOString()} - ${fechaFin.toISOString()}`);

    const q = query(
      collection(db, "pl_adelantos"),
      where("ruc_empresa", "==", ruc),
      where("empleado_id", "==", empleadoId)
      // Buscamos todos para filtrar por fecha. Filtramos por estado "POR DESCONTAR", "PENDIENTE", "APLICADO" o "DESCONTADO"
    );

    const snapshot = await getDocs(q);
    const adelantos = [];
    
    snapshot.forEach((doc) => {
      const data = doc.data();
      let fechaAdelanto;

      // Manejar diferentes formatos de fecha
      if (data.fecha instanceof Date) {
        fechaAdelanto = data.fecha;
      } else if (data.fecha && typeof data.fecha.toDate === 'function') {
        // Timestamp de Firestore
        fechaAdelanto = data.fecha.toDate();
      } else if (typeof data.fecha === 'string' && data.fecha.includes('-')) {
        // Formato esperado YYYY-MM-DD
        const [a, m, d] = data.fecha.split("-").map(Number);
        fechaAdelanto = new Date(a, m - 1, d);
      } else {
        // Fallback para otros formatos
        fechaAdelanto = new Date(data.fecha);
      }

      // Verificar si el adelanto está dentro del período de la planilla
      if (fechaAdelanto >= fechaInicio && fechaAdelanto <= fechaFin) {
        adelantos.push({
          id: doc.id,
          ...data
        });
        console.log(`✅ [obtenerAdelantosPendientes] Adelanto incluido: ${doc.id}, Fecha: ${data.fecha}, Monto: ${data.monto}, Estado: ${data.estado}`);
      } else {
        console.log(`ℹ️ [obtenerAdelantosPendientes] Adelanto excluido (fuera de rango): ${doc.id}, Fecha: ${data.fecha}, Estado: ${data.estado}`);
      }
    });

    console.log(`📊 [obtenerAdelantosPendientes] Total adelantos calificados: ${adelantos.length}`);
    return adelantos;
  } catch (error) {
    console.error("Error al obtener adelantos pendientes:", error);
    return [];
  }
};

// Función para calcular el total de adelantos a descontar
export const calcularTotalAdelantos = (adelantos) => {
  if (!adelantos || !Array.isArray(adelantos)) {
    return 0;
  }
  return adelantos.reduce((total, adelanto) => total + (adelanto.monto || 0), 0);
};

// Función para marcar adelantos como aplicados
export const marcarAdelantosComoAplicados = async (adelantosIds, usuario) => {
  try {
    const { doc, updateDoc, getFirestore } = await import("firebase/firestore");
    const firebaseApp = (await import("../firebase/credenciales")).default;
    const db = getFirestore(firebaseApp);

    const promises = adelantosIds.map(async (id) => {
      const adelantoRef = doc(db, "pl_adelantos", id);
      await updateDoc(adelantoRef, {
        estado: "DESCONTADO",
        descontado_en: new Date(),
        updated_at: new Date()
      });
    });

    await Promise.all(promises);
    console.log(`Marcados ${adelantosIds.length} adelantos como descontados`);
  } catch (error) {
    console.error("Error al marcar adelantos como aplicados:", error);
  }
};

// La función calcularPlanillaVacaciones ha sido removida.

/**
 * Calcula liquidación completa para un empleado
 * @param {Object} empleado - Datos del empleado
 * @param {string} empleadoId - ID del empleado
 * @param {string} fechaCese - Fecha de cese YYYY-MM-DD
 * @param {string} tipoCese - Tipo de cese (despido, renuncia, etc.)
 * @param {string} regimenAplicar - Régimen laboral
 * @param {Object} configuracionRegimen - Configuración del régimen
 * @param {Object} config - Configuración general
 * @param {Object} bonosMap - Mapa de bonos
 * @param {Function} calcularLiquidacion - Función de cálculo
 * @param {Object} usuario - Usuario actual
 * @returns {Object} - Detalle completo de liquidación
 */
export const calcularPlanillaLiquidacion = async (
  empleado,
  empleadoId,
  fechaCese,
  tipoCese,
  regimenAplicar,
  configuracionRegimen,
  config,
  bonosMap,
  calcularLiquidacion,
  usuario,
  historial5ta = null // Nuevo parámetro opcional
) => {
  try {
    console.log("🔍 calcularPlanillaLiquidacion - Iniciando cálculo", {
      empleadoId,
      fechaCese,
      tipoCese,
      regimenAplicar,
      historial5ta
    });

    // Preparar datos del empleado
    const empleadoParaCalculo = {
      ...empleado,
      nombres: empleado.nombre || empleado.nombres,
      apellidos: empleado.apellido || empleado.apellidos,
      sueldo_base: empleado.sueldo_basico || empleado.sueldo_base
    };

    // Calcular remuneración computable (sueldo + asignación familiar)
    const sueldoBase = Number(empleado.sueldo_base || empleado.sueldo_basico || 0);
    const asignacionFamiliar = empleado.asignacion_familiar === true &&
      configuracionRegimen.parametros[regimenAplicar]?.asignacion_familiar?.aplica
      ? Number(config.asignacion_familiar || 0)
      : 0;

    const rc = sueldoBase + asignacionFamiliar;

    // Preparar régimen
    const regimen = {
      codigo: regimenAplicar,
      ...configuracionRegimen.parametros?.[regimenAplicar]
    };

    // Opciones de cálculo
    const opcionesCalculo = {
      rc: rc,
      vacacionesPendientes: Number(empleado.vacaciones_pendientes || 0),
      gratificacionPagada: false // Por defecto, calcular gratificación trunca
    };

    // Llamar a la función de cálculo de liquidación
    const resultado = await calcularLiquidacion(
      empleadoParaCalculo,
      regimen,
      {
        rmv: Number(config.sueldo_minimo || 1130),
        uit: Number(config.uit || 5500)
      },
      config,
      fechaCese,
      tipoCese,
      opcionesCalculo,
      historial5ta // Pasar historial a la función de cálculo
    );

    console.log("🔍 calcularPlanillaLiquidacion - Resultado:", resultado);

    // Verificar si hay error
    if (resultado.error) {
      throw new Error(resultado.mensaje || "Error en el cálculo de liquidación");
    }

    // Construir detalle del empleado con los datos de liquidación
    const { bonos } = calcularBonos(empleado, bonosMap);

    const descuentosFaltas = { monto_descuento: 0, dias_descontados: 0 };
    const descuentosTardanzas = {
      monto_descuento: 0,
      dias_con_tardanza: 0,
      minutos_totales: 0,
    };

    const datosAsistencia = {
      faltas: { injustificadas: 0, justificadas: 0 },
      permisos: { sin_goce: 0, con_goce: 0 },
      tardanzas: []
    };

    // Estructurar descuentos desde el resultado de liquidación
    const descuentos = {
      sistema_pension: resultado.descuentos?.sistemaPension || {},
      renta_5ta: resultado.descuentos?.impuestoRenta || 0,
      essalud_vida: 0,
      descuento_faltas: 0,
      descuento_dso: 0,
      descuento_tardanzas: 0,
      total: resultado.totales?.deducciones || 0,
    };

    const aportaciones = {
      essalud: resultado.descuentos?.aportesEmpleador || 0,
      sctr: 0,
      vida_ley: 0,
      total: resultado.descuentos?.aportesEmpleador || 0,
    };

    // Retornar detalle estructurado para PlanillaTable
    // IMPORTANTE: Diferenciar entre conceptos afectos e inafectos
    const salarioMesCese = resultado.salarioMesCese?.bruto || 0;
    const vacaciones = resultado.vacaciones?.bruto || 0;
    const gratificacion = resultado.gratificacion?.bruto || 0;
    const cts = resultado.cts?.bruto || 0;
    const indemnizacion = resultado.indemnizacion?.bruto || 0;

    // Conceptos AFECTOS a AFP/ONP y Renta 5ta: Salario + Vacaciones
    const totalIngresosAfectos = salarioMesCese + vacaciones;

    // Conceptos INAFECTOS: Gratificación + CTS + Indemnización
    const totalIngresosInafectos = gratificacion + cts + indemnizacion;

    // Total Bruto: Suma de todos los conceptos
    const totalIngresosBruto = totalIngresosAfectos + totalIngresosInafectos;

    return {
      empleado_id: empleadoId,
      nombres: `${empleado.nombres || ""} ${empleado.apellidos || ""}`,
      sueldo_base: 0,
      asignacion_familiar: 0,
      bonos,
      regimen_pensionario: empleado?.regimen_pensionario || "NO ASIGNADO",
      regimen_aplicado: regimenAplicar,

      // Conceptos de liquidación
      salario_mes_cese: salarioMesCese,
      gratificacion: gratificacion,
      cts: cts,
      vacaciones: vacaciones,
      indemnizacion: indemnizacion,

      // Totales
      total_ingresos: totalIngresosBruto,
      total_ingresos_bruto: totalIngresosBruto,
      total_ingresos_afectos: totalIngresosAfectos, // CORREGIDO: Solo salario + vacaciones

      descuentos,
      aportaciones,
      total_descuentos: resultado.totales?.deducciones || 0,
      total_aportaciones: aportaciones.total,
      costo_laboral_total: Number((totalIngresosBruto + (aportaciones.total || 0)).toFixed(2)), // En liquidación no hay provisiones nuevas, ya se están pagando

      // Datos de asistencia (vacíos para liquidación)
      faltas_injustificadas: 0,
      faltas_justificadas: 0,
      permisos_sin_goce: 0,
      permisos_con_goce: 0,
      dias_faltantes: 0,
      dias_tardanzas: 0,
      minutos_tardanza_total: 0,

      adelantos: [],
      provisiones: { vacaciones: 0, gratificaciones: 0, cts: 0, total: 0 },

      neto_pagar: resultado.totales?.neto || 0,

      // Metadata de liquidación
      liquidacion_metadata: {
        fecha_cese: fechaCese,
        tipo_cese: tipoCese,
        rc: rc,
        tiempo_servicio: resultado.metadata?.tiempoServicio || {},
        total_ingresos_inafectos: totalIngresosInafectos // Agregar para referencia
      }
    };

  } catch (error) {
    console.error("❌ Error en calcularPlanillaLiquidacion:", error);
    throw error;
  }
};
